import photoView from '../index.js';
import _ from 'lodash';

export default (route, params, options) => ({
    ...photoView(route, params, options),

    selectionIndexRanges: [],
    pinnedImageId: null,
    _savedScrollTop: null,

    init() {
        window.removeEventListener('selected-color', ({ detail: [color, teamId] }) =>
            this.setColorValues(color, teamId),
        );

        this.reset();
        this.start();

        this.initWatches();

        window.addEventListener('selected-color', ({ detail: [color, teamId] }) =>
            this.setColorValues(color, teamId),
        );
    },

    toggleOverrideColors({ detail }) {
        this.params.overrideColorCorrection = detail.override ? 1 : 0;

        this.refreshList({ detail });
    },

    initWatches() {
        this.$watch('tilesState', (tiles) => {
            this.selectedTiles = Object.values(tiles)
                .filter((tile) => tile?.selected == !this.allSelected)
                .map((tile) => tile?.id);

            if (this.allSelected && this.selectedTiles.length === 1 && this.totalTiles === 1) {
                this.$dispatch('pictures::deselect-all');
            }

            this.dispatchSelectEvent();
        });
    },

    setColorValues(color, teamId) {
        if (!this.metadata) {
            this.metadata = {};
        }

        if (!this.metadata.team_colors) {
            this.metadata.team_colors = {};
        }

        this.metadata.team_colors[teamId] = color;

        const elements = document.querySelectorAll(`[data-team-id="${teamId}"]`);

        requestAnimationFrame(() => {
            elements.forEach((el) => {
                el.style.backgroundColor = color;
                el.style.color = color === '#FFFFFF' || color === '#D6D6D7' ? 'black' : 'white';
            });
        });
    },

    fillTilesEmptyState(segmentAmount, id) {
        for (let segmentIndex = 1; segmentIndex <= segmentAmount; segmentIndex++) {
            for (let tileIndex = 1; tileIndex <= this.segmentSize; tileIndex++) {
                if (
                    Object.values(this.tilesState[`${id}${segmentIndex}${tileIndex}`] ?? {}).length
                ) {
                    continue;
                }
                this.tilesState[`${id}${segmentIndex}${tileIndex}`] = {};
            }
        }
    },

    fillTilesState(segments, sectionId = null) {
        return new Promise((resolve) => {
            segments.forEach((segment) => {
                segment.images.forEach((image, imageIndex) => {
                    if (image.pinned_at) {
                        this.pinnedImageId = image.id;
                    }

                    this.tilesState[`${segment.segmentId}${imageIndex + 1}`] = {
                        ...this.tileStateObject(image, sectionId, segment.segmentId),
                        ...this.tilesState[`${segment.segmentId}${imageIndex + 1}`],
                    };
                });
            });

            resolve();
        });
    },

    refreshList({ detail: { keepSelected } = {} }) {
        const except = keepSelected ? ['allSelected', 'tilesState'] : [];

        this.pinnedImageId = null;
        this.reset(...except);
        this.start();
    },

    refreshListKeepingScroll(event) {
        if (this._savedScrollTop === null) {
            this._savedScrollTop = this.container?.scrollTop ?? null;
        }

        this.refreshList(event);
    },

    async loadInitialScreen() {
        const savedScrollTop = this._savedScrollTop;

        this._savedScrollTop = null;

        const { groups, total } = await this.getSectionsGrouped();

        this.totalTiles = total;

        if (!total) {
            this.mountEmptyState();
            return;
        }

        await this.createGroups(groups);

        this.setGridScrollHeight();

        if (savedScrollTop) {
            await this.restoreScrollPosition(savedScrollTop);
        }
    },

    async restoreScrollPosition(scrollTop) {
        await new Promise((resolve) => requestAnimationFrame(resolve));

        this.container.scrollTop = scrollTop;
        this.scrollDirection = 'down';

        this.unmountSectionsOutOfView();

        await this.loadSectionEmpty();

        this.loadSectionData();
    },

    async setNextSection(section) {
        return new Promise((resolve) => {
            this.sections[section.id].shouldMountEmptyState = false;

            requestAnimationFrame(() => {
                const sectionBelow = [
                    ...this.gridScrollNode.querySelectorAll('div.section'),
                ].find((sectionDiv) => this.sectionIdOf(sectionDiv) > Number(section.id));

                sectionBelow
                    ? sectionBelow.insertAdjacentHTML('beforebegin', section.html)
                    : this.gridScrollNode.insertAdjacentHTML('beforeend', section.html);

                resolve();
            });
        });
    },

    sectionIdOf(sectionDiv) {
        return Number(this.splitId(sectionDiv.id));
    },

    unmountSectionsOutOfView() {
        const viewTop = this.container.scrollTop;
        const viewBottom = viewTop + this.container.clientHeight;

        this.gridScrollNode.querySelectorAll('div.section').forEach((sectionDiv) => {
            const section = this.sections[this.sectionIdOf(sectionDiv)];

            const isVisible =
                section && section.top <= viewBottom && section.top + section.height >= viewTop;

            if (isVisible) {
                return;
            }

            this.cancelPendingImageRequests(sectionDiv);
            this.gridScrollNode.removeChild(sectionDiv);

            if (section) {
                section.shouldMountEmptyState = true;
                section.shouldUpdate = true;
            }
        });
    },

    handlePinClick(selectedImages) {
        const imageId = selectedImages[0];

        if (this.$wire.pinnedImageId && imageId !== this.$wire.pinnedImageId) {
            this.$wireui.confirmDialog({
                title: this.$el.dataset.confirmTitle,
                description: this.$el.dataset.confirmDescription,
                icon: 'question',
                accept: {
                    label: this.$el.dataset.confirmAccept,
                    execute: () => { this.$wire.pinPhoto(imageId); this.deselectAll(); },
                },
                reject: { label: this.$el.dataset.confirmReject },
            });
        } else {
            this.$wire.pinPhoto(imageId);
            this.deselectAll();
        }
    },

    toggleAll({ detail }) {
        const { allSelected } = detail;

        allSelected ? this.selectAll() : this.deselectAll();
    },

    selectAll() {
        Object.values(this.tilesState).forEach((tile) => {
            if (tile) {
                tile.selected = true;
            }
        });

        this.allSelected = true;
        this.selectionIndexRanges = [];
    },

    deselectAll() {
        Object.values(this.tilesState).forEach((tile) => {
            if (tile) {
                tile.selected = false;
            }
        });

        this.allSelected = false;
        this.selectionIndexRanges = [];
    },

    reset(...except) {
        const defaults = {
            selectedTiles: [],
            storageData: {},
            allSelected: false,
            totalTiles: 0,
            tilesState: {},
            lastSelectedTileIndex: null,
            selectionIndexRanges: [],
            sections: [],
            sectionSize: 0,
            segmentSize: 0,
            gridScrollNode: null,
            debounceResize: null,
            debounceRender: null,
            scrollDirection: null,
            container: null,
        };

        Object.entries(defaults).forEach(([key, value]) => {
            if (!except.includes(key)) {
                this[key] = value;
            }
        });

        if (!except.includes('gridContainer')) {
            const grid = document.getElementById(this.options.gridContainerId);
            if (grid) {
                grid.innerHTML = '';
            }
        }
    },

    getTeamsTag(teamsSelected, currentTeams = []) {
        if (_.isEmpty(teamsSelected)) {
            return teamsSelected;
        }

        return _.uniqBy([...currentTeams, ..._.values(teamsSelected)], 'id');
    },

    async updateTag({ detail: { turnTo } }) {
        const keepSelected = Object.entries(turnTo).length === 0;

        if (!this.totalTilesCount && !this.allSelected) {
            return;
        }

        this.$nextTick(() => {
            Object.entries(this.tilesState).forEach(([key, tile]) => {
                if (!tile?.selected) {
                    return;
                }

                const index = parseInt(key);

                tile['teams'] = this.getTeamsTag(turnTo, tile['teams']);

                tile.selected = keepSelected;

                this.updateTileTag(index);
            });

            this.allSelected = keepSelected ? this.allSelected : false;
        });
    },

    updateTileTag(index) {
        const tagElements = this.tagElements(index);

        const tileTags = document.getElementById(`tile-tags-${index}`);
        if (tileTags) {
            tileTags.innerHTML = tagElements;
        }
    },

    getTeams(tileIndex) {
        return this.tilesState[tileIndex]?.teams;
    },

    getColor(team) {
        return _.get(this.metadata, `team_colors.${team.id}`, 'black');
    },

    tagElements(index = null) {
        const teams = this.getTeams(index);

        return teams
            .map((team) => {
                const color = this.getColor(team);
                const textColor = color === '#FFFFFF' || color === '#D6D6D7' ? 'black' : 'white';

                return `
                <div
                    data-team-id="${team.id}"
                    class="p-1 px-2 rounded-md border border-white font-semibold flex gap-1"
                    style="background-color: ${color}; color: ${textColor}">
                    <div class="flex-1 overflow-hidden whitespace-nowrap text-ellipsis">
                        ${team.name}
                    </div>
                </div>
            `;
            })
            .join('');
    },

    teamTagsGroup(index) {
        const tagElements = this.tagElements(index);

        return `
            <div id="tile-tags-${index}" class="flex flex-col gap-1 text-2xs -ml-1 mr-2 overflow-hidden absolute left-2 bottom-2" style="max-width: 60%;">
                ${tagElements}
            </div>
        `;
    },

    dispatchSelectEvent() {
        this.$dispatch('select', {
            selectedIds: this.selectedTiles,
            selectedIndexes: this.getSelectedIndexes(),
        });
    },

    async selectTilesAcrossSegments(firstIndex, lastIndex, startSectionId, endSectionId) {
        const { offset, limit } = this.calculateSelectionParams(
            startSectionId,
            endSectionId,
            firstIndex,
            lastIndex,
        );

        this.selectionIndexRanges.push({
            start: offset,
            end: offset + limit - 1,
        });

        this.dispatchSelectEvent();
    },

    getSelectedIndexes() {
        const indexes = new Set();

        this.selectionIndexRanges.forEach(({ start, end }) => {
            for (let index = start; index <= end; index++) {
                indexes.add(index);
            }
        });

        Object.entries(this.tilesState).forEach(([tileIndex, tile]) => {
            if (tile?.selected && tile?.id) {
                indexes.add(this.getTileAbsoluteIndex(tileIndex, tile));
            }
        });

        if (this.unselectedAfterShiftSelect.length) {
            Object.entries(this.tilesState).forEach(([tileIndex, tile]) => {
                if (tile?.id && this.unselectedAfterShiftSelect.includes(tile.id)) {
                    indexes.delete(this.getTileAbsoluteIndex(tileIndex, tile));
                }
            });
        }

        return [...indexes].sort((first, second) => first - second);
    },

    getTileAbsoluteIndex(tileIndex, tile) {
        const {
            section,
            segment,
            tile: tileNumber,
        } = this.separateTileNumber(tile?.sectionId, tileIndex);

        const previousSectionSize = this.sections[section]?.previousSectionSize ?? 0;

        return previousSectionSize + (segment - 1) * this.segmentSize + (tileNumber - 1);
    },

    pinIconHtml(image) {
        if (!image.pinned_at) {
            return '';
        }

        return `
            <button
                class="cursor-pointer rounded-full p-1 hover:opacity-75 bg-gray-100"
                data-pin-image-id="${image.id}"
                title="Click to unpin"
            >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 2a1 1 0 0 1 .894.553l1 2A1 1 0 0 1 17 6h-1v5.382l1.447 2.894A1 1 0 0 1 17 16h-4v5a1 1 0 1 1-2 0v-5H7a1 1 0 0 1-.894-1.447L8 11.382V6H7a1 1 0 0 1-.894-1.447l1-2A1 1 0 0 1 8 2h8z"/>
                </svg>
            </button>
        `;
    },

    topImageItems(image, index) {
        const selectCheckbox = this.options.selectable
            ? `
            <div class="absolute top-2 right-2 h-6 w-6 text-center bg-white border-none rounded-full shadow-soft block" x-data>
                <input
                    type="checkbox"
                    class="h-4 w-4 rounded-full p-1 border-2 border-gray-300 text-green-600 form-checkbox focus:ring-green-500"
                    :checked="isSelected('${index}')"
                />
            </div>
        `
            : '';

        const highResLink = this.options.showHighResLink
            ? `
            <a
                href="${image.high_res}"
                target="_blank"
                rel="noopener noreferrer"
                x-on:click="event.stopPropagation();"
                class="cursor-pointer rounded-full p-1 button hover:opacity-75 bg-gray-100"
                id="high-${index}">
                <svg class="w-5 h-5 text-blue-500" x-ref="previewIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
                </svg>
            </a>
            `
            : '';

        return `
            <div class="top-buttons buttons absolute top-0 left-0 w-full h-10 flex items-center justify-between px-2">
              ${selectCheckbox}
              <div class="flex gap-1 items-center">
                ${highResLink}
                ${this.pinIconHtml(image)}
              </div>
            </div>
        `;
    },

    addSelectClick(sectionDiv) {
        sectionDiv.querySelectorAll('div[data-tile-id]').forEach((el) => {
            el.addEventListener('click', (event) => {
                if (event.target.closest('[data-pin-image-id]')) {
                    return;
                }
                this.selectTile(el.dataset.tileId, event.shiftKey);
            });
        });

        sectionDiv.querySelectorAll('[data-pin-image-id]').forEach((pinIcon) => {
            pinIcon.addEventListener('click', (event) => {
                event.stopPropagation();
                this.$dispatch('pin-photo', { imageId: parseInt(pinIcon.dataset.pinImageId, 10) });
            });
        });
    },

    getTileElement(image, index) {
        const tileDiv = this.tileTemplate.cloneNode(true);

        tileDiv.style.width = `${this.tileSize.width}px`;
        tileDiv.style.height = `${this.tileSize.height}px`;
        tileDiv.setAttribute(
            'x-bind:class',
            `{
            'bg-gray-300 opacity-50': isDeleted('${index}'),
            'bg-gray-700': !isDeleted('${index}'),
            'border-green-500': isSelected('${index}'),
            'border-transparent': !isSelected('${index}'),
        }`,
        );

        tileDiv.setAttribute('data-tile-id', index);

        const img = tileDiv.querySelector('img');
        img.src = image.thumb;
        img.setAttribute('data-image-id', image.id);

        if (this.options.preview) {
            img.setAttribute('x-tooltip.placement.left-start', this.fillPreviewMode(image));
        }

        tileDiv.insertAdjacentHTML('beforeend', this.fillActionsAndTags(image, index));

        return tileDiv;
    },

    fillActionsAndTags(image, index) {
        return `${this.topImageItems(image, index)}${this.bottomImageItems(image, index)}`;
    },

    bottomImageItems(image, index) {
        const imageNameBadge = `
            <span class="text-2xs h-6 gap-1 flex items-center text-gray-900 font-semibold py-0.5 bg-white px-3 rounded-xl -mb-1 -ml-1">
              ${image.name}
            </span>
        `;

        return `
            <div id="image-bottom-${index}" class="buttons absolute bottom-0 w-full h-10 flex items-center px-2 right-0 justify-end">
              ${this.teamTagsGroup(index)}
              ${imageNameBadge}
            </div>
        `;
    },
});
